import type { DateFormatId } from '@talleflow/locale-fields';

/**
 * Display format for calendar-day merge tags.
 *
 * Default (`long_mdy`) is `October 2, 2026`. Pass a business `DateFormatId`
 * so every consumer (api, web preview, portal, mobile, custom fields, Trigger
 * worker) follows the same setting.
 */
export const MERGE_TAG_DATE_LOCALE = 'en-US';

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

interface CalendarDayParts {
  year: number;
  month: number;
  day: number;
}

function parseCalendarDay(ymd: string | null | undefined): CalendarDayParts | null {
  if (typeof ymd !== 'string') return null;
  const match = CALENDAR_DAY.exec(ymd.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth) return null;
  return { year, month, day };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function formatLongCalendarDay(parts: CalendarDayParts, format: 'long_mdy' | 'long_dmy'): string {
  const anchor = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  if (Number.isNaN(anchor.getTime())) return '';
  return new Intl.DateTimeFormat(format === 'long_dmy' ? 'en-GB' : MERGE_TAG_DATE_LOCALE, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(anchor);
}

/**
 * Formats a `YYYY-MM-DD` calendar day for display. Built and formatted in UTC so
 * the day never shifts with the runtime timezone. Invalid or partial input
 * returns an empty string, never `Invalid Date`.
 */
export function formatMergeTagCalendarDay(
  ymd: string | null | undefined,
  format: DateFormatId = 'long_mdy'
): string {
  const parts = parseCalendarDay(ymd);
  if (!parts) return '';

  switch (format) {
    case 'iso':
      return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
    case 'numeric_mdy':
      return `${pad2(parts.month)}/${pad2(parts.day)}/${parts.year}`;
    case 'numeric_dmy':
      return `${pad2(parts.day)}/${pad2(parts.month)}/${parts.year}`;
    case 'long_dmy':
    case 'long_mdy':
      return formatLongCalendarDay(parts, format);
    default: {
      const unreachable: never = format;
      return unreachable;
    }
  }
}
